import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Hooks
import useKeyPress from '../../hooks/use-key-press';
import useOutsideClick from '../../hooks/use-outside-click';

// Styles
import './generic-modal.scss';

// Components
import CloseButton from './components/close-button/close-button';
import OneRosterManageStudents from './components/one-roster-manage-students/one-roster-manage-students';

// Helpers
import { ESCAPE_KEY_CODE, ESCAPE_KEY_NAME } from '../../helpers/enums';

export const MODAL_TYPE = {
    floating: 'floating'
};

/**
 *
 * @param title
 * @param children: content of the pop-up
 * @param closeHandler: action to perform on close
 * @param parentClasses: additional classes to apply to the modal
 * @param containerStyle: inline styles to apply to the modal
 * @param type:
 *  -'floating': will display a centered pop-up with an overlay behind
 * @returns {*}
 * @constructor
 */
function GenericModal({
    title,
    children,
    closeHandler,
    parentClasses,
    containerStyle,
    type,
    isOneRosterActivatedSchool
}) {
    const { i18n } = useLocalisation();

    const [escapeKeyPressed] = useKeyPress({
        targetKeyName: ESCAPE_KEY_NAME,
        targetKeyCode: ESCAPE_KEY_CODE
    });

    // Effect for handling escape key press
    useEffect(() => {
        if (escapeKeyPressed && closeHandler) {
            closeHandler();
        }
    }, [escapeKeyPressed]);

    const containerClasses = `modal${
        type && type === MODAL_TYPE.floating ? ' modal--floating' : ''} ${parentClasses || ''}`;

    // Click outside
    const modalContainerRef = useRef(null);
    useOutsideClick(
        [modalContainerRef],
        closeHandler
    );

    const getOneRosterStyles = () => {
        if (isOneRosterActivatedSchool) {
            return ({
                padding: '20px 24px',
                border: '2px solid #f7d064',
                borderRadius: '16px'
            });
        }
        return null;
    };

    const modalTemplate = (
        <div ref={modalContainerRef}>
            <FocusTrap>
                <div
                    style={{ ...containerStyle, ...getOneRosterStyles() }}
                    className={containerClasses}>
                    {
                        isOneRosterActivatedSchool ?
                            <OneRosterManageStudents i18n={i18n} title={title} />
                            :
                            <Fragment>
                                <div className="modal__header">
                                    <h2 className="modal__title">{title}</h2>
                                    <CloseButton closeHandler={closeHandler} i18n={i18n} />
                                </div>
                                {children}
                            </Fragment>
                    }
                </div>
            </FocusTrap>
        </div>
    );

    return type === MODAL_TYPE.floating ? (
        <aside className="modal__overlay">
            {modalTemplate}
        </aside>
    ) : (
        modalTemplate
    );
}

GenericModal.propTypes = {
    title: PropTypes.object,
    children: PropTypes.any,
    closeHandler: PropTypes.func.isRequired,
    containerStyle: PropTypes.object,
    parentClasses: PropTypes.string,
    type: PropTypes.string,
    isOneRosterActivatedSchool: PropTypes.bool
};

export default GenericModal;



// WEBPACK FOOTER //
// ./components/generic-modal/generic-modal.js